import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog"

// Create a brand-new course straight into this track (plan 2026-09-23-001).
// The server creates it as a draft, appends it to the end of the track, and
// opens the new course so the admin can add lessons.
export function NewCourseDialog({ open, onOpenChange, trackId, trackTitle, categories = [], defaultCategoryId }) {
  const form = useForm({
    title: "",
    description: "",
    course_category_id: defaultCategoryId ? String(defaultCategoryId) : "",
    visibility: "all_members",
  })

  const submit = (e) => {
    e.preventDefault()
    form.post(`/app/admin/learning_tracks/${trackId}/track_courses/new_course`, { preserveScroll: true })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) form.clearErrors()
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={submit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">New course in this track</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Creates a draft course at the end of {trackTitle}. You add its lessons next, then publish it.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="new-course-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-course-title"
                className="bg-white border-zinc-300 text-zinc-900"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && <p className="text-sm text-red-600">{form.errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-course-description">Description</Label>
              <Textarea
                id="new-course-description"
                className="bg-white border-zinc-300 text-zinc-900 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-course-category">
                  Category <span className="text-red-500">*</span>
                </Label>
                <Select value={form.data.course_category_id} onValueChange={(v) => form.setData("course_category_id", v)}>
                  <SelectTrigger id="new-course-category" className="w-full bg-white border-zinc-300 text-zinc-900">
                    <SelectValue placeholder="Choose a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.errors.course_category && <p className="text-sm text-red-600">{form.errors.course_category}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-course-visibility">Who can see it</Label>
                <Select value={form.data.visibility} onValueChange={(v) => form.setData("visibility", v)}>
                  <SelectTrigger id="new-course-visibility" className="w-full bg-white border-zinc-300 text-zinc-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_members">All members</SelectItem>
                    <SelectItem value="coaches_only">Coaches only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing || !form.data.course_category_id}>
              {form.processing ? "Creating..." : "Create course"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
